// frontend/app/exhibit/v2/screens/RealOrAIScreen.js

import React, { useEffect, useRef, useState } from "react";
import { Image, Pressable, StyleSheet, View } from "react-native";
import { LocalizedText as Text } from "../i18n/ExhibitLocaleContext";
import { tx } from "../i18n/exhibitLocale";

import { getExhibitAttemptStats } from "../../api/aiExhibitAnalyticsClient";
import { aiExplainedAssetUrl, getRealOrAIProductionRounds } from "../../api/aiExhibitContentClient";

const PROTOTYPE_ROUNDS = [
  {
    id: "video",
    medium: "Short video",
    kind: "text",
    icon: "🎬",
    title: {
      el: "Two video openings. A computer wrote one.",
      hs: "Two short-video openings. One script was generated.",
    },
    sub: {
      el: "Read the words. Which one did a computer write?",
      hs: "Read the captions — which one did a machine write?",
    },
    a: {
      label: "Video A · captions",
      text: "okay so — wait, is it recording? ok. THIS is the pothole. Third time this month. Look at it.",
    },
    b: {
      label: "Video B · captions",
      text: "Discover the hidden dangers lurking on your daily commute. In today's video, we explore what every driver needs to know.",
    },
    generated: "B",
    clue: { el: "The clue is the stumble.", hs: "The clue is the stumble." },
    why: {
      el: "The computer never trips over its words. Real people fumble the camera and start in the middle. Stumbles are a clue it is real.",
      ms: "Computer-written scripts open with a hook and never stumble. Real people fumble the camera, repeat themselves, and start mid-thought.",
      hs: "Generated scripts open with a hook and never stumble. Real people fumble the camera, repeat themselves, and start mid-thought.",
    },
  },
  {
    id: "news",
    medium: "News text",
    kind: "news",
    icon: "📰",
    title: {
      el: "One of these news stories was made up by a computer.",
      hs: "One of these stories was generated.",
    },
    sub: {
      el: "Talk it over. Then touch the one no reporter wrote.",
      hs: "Talk it over — then touch the one you think no reporter wrote.",
    },
    a: {
      label: "Story A",
      head: "City pool stays open two extra weeks after cool-summer surplus",
      text: "Parks staff said Tuesday the aquatics budget came in under projections, letting the main pool run through mid-September.",
    },
    b: {
      label: "Story B",
      head: "“Miracle” pool chemical lets city cut lifeguards, save millions",
      text: "Officials are reportedly thrilled by an additive that makes water “virtually un-drownable,” according to sources familiar with the plan.",
    },
    generated: "B",
    clue: {
      el: "The clue is who said it.",
      ms: "The clue wasn't the writing. It was who said it.",
      hs: "The clue wasn't the writing. It was the sourcing.",
    },
    why: {
      el: "Story B sounds fancy. But it never says who really said it. A computer can copy fancy words. It cannot give you a real person to check with.",
      ms: "Story B sounds polished and points to “sources familiar.” A computer can copy a newspaper’s style perfectly. What it cannot copy is a real, checkable source. Polished is not the same as true.",
      hs: "Story B sounds polished and cites “sources familiar.” Generated text can copy a newsroom's style perfectly — what it can't copy is a checkable source. Polished ≠ true.",
    },
  },
  {
    id: "outrage",
    medium: {
      el: "Bonus round: made to make you mad",
      hs: "Bonus round: engineered outrage",
    },
    kind: "text",
    icon: "😤",
    title: {
      el: "Two posts about the same park. One was built to make you angry.",
      hs: "Two posts about the same park. One was engineered.",
    },
    sub: {
      el: "Both are made up for this game. Which one wants you angry?",
      hs: "Both are invented for this exhibit. Which one was built to make you angry?",
    },
    a: {
      label: "Post A",
      text: "New benches at Miller Park. Kinda ugly tbh, but there's way more of them, so.",
    },
    b: {
      label: "Post B",
      text: "They RIPPED OUT every bench at Miller Park overnight and NOBODY was told. Share this before it gets buried!!!",
    },
    generated: "B",
    clue: {
      el: "Loud is not the same as true.",
      ms: "Loud ≠ true.",
      hs: "Provocative ≠ true.",
    },
    why: {
      el: "Angry posts are the easiest thing to make, because people share them before they check. CAPITAL LETTERS, a bad guy, and “share this now!” are the recipe. And what it says barely matters.",
      ms: "Angry posts are the cheapest thing to make, because they spread before anyone checks. Capital letters, a villain, and “share before it gets buried” are the recipe. And the facts barely matter.",
      hs: "Outrage is the cheapest thing to generate, because it spreads before anyone checks. Capital letters, a villain, and “share before it's buried” are the recipe — the content barely matters.",
    },
  },
];

function tierText(value, tier) {
  if (value == null || typeof value === "string") return value ?? "";
  if (tier === "el") return value.el ?? value.ms ?? value.hs ?? "";
  if (tier === "ms") return value.ms ?? value.hs ?? value.el ?? "";
  return value.hs ?? value.ms ?? value.el ?? "";
}

function votePick(vote) {
  if (!vote) return null;
  if (vote.A === vote.B) return vote.last;
  return vote.A > vote.B ? "A" : "B";
}

function voteCountText(n, language = "en") {
  if (language === "es") return `${n} ${n === 1 ? "voto" : "votos"} para esta opción`;
  return `${n} ${n === 1 ? "vote" : "votes"} for this one`;
}

function roundCorrectAnswer(round) {
  return round?.correctAnswer || round?.generated || "A";
}

const REVEAL_NARRATION_BY_ROUND_ID = Object.freeze({
  photo: "v2_real_or_ai_photo_reveal",
  chat: "v2_real_or_ai_chat_reveal",
  video: "v2_real_or_ai_video_reveal",
  news: "v2_real_or_ai_news_reveal",
  voice: "v2_real_or_ai_voice_reveal",
  outrage: "v2_real_or_ai_manipulation_reveal",
});

function nextRoundLabel(rounds, roundIndex, tier, language = "en") {
  const nextRound = rounds[roundIndex + 1];
  if (!nextRound) return tx(language, tier === "el" ? "See your score →" : "See how you did →");
  const medium = tx(language, tierText(nextRound.medium, tier) || "next round");
  return language === "es" ? `Siguiente: ${medium} →` : `Next: ${medium} →`;
}

function TextCard({ round, side, votes, leading, onVote, tier, language = "en" }) {
  const data = round[side.toLowerCase()];
  const count = votes?.[side] || 0;
  const actionLabel = tierText(round.actionLabel, tier) || (tier === "el" ? "The computer made this one" : "This one is AI");

  return (
    <Pressable
      onPress={() => onVote(side)}
      style={({ pressed }) => [
        styles.textCard,
        leading === side && styles.leadingCard,
        pressed && styles.pressed,
      ]}
    >
      <Text style={styles.cardLabel}>
        {round.icon ? `${round.icon} ` : ""}{tx(language, data.label).toUpperCase()}
      </Text>

      {round.kind === "news" && (
        <Text style={styles.newsHead}>{data.head}</Text>
      )}

      {round.kind === "voice" && (
        <Text style={styles.audioNote}>TRANSCRIPT · fixed Saras audio comes in the narration pass</Text>
      )}

      <Text style={styles.cardBody}>{data.text}</Text>

      <View style={styles.cardSpacer} />

      <Text style={styles.voteCount}>{count ? voteCountText(count, language) : " "}</Text>

      <View style={styles.voteButton}>
        <Text style={styles.voteButtonText}>
          {actionLabel}
          {count ? ` · ${count}` : ""}
        </Text>
      </View>
    </Pressable>
  );
}

function PhotoChoice({ round, side, count, leading, onVote, tier, language = "en" }) {
  const data = round?.[String(side).toLowerCase()] || {};
  return (
    <View style={[styles.photoChoice, leading === side && styles.leadingPhotoChoice]}>
      <Pressable onPress={() => onVote(side)} style={({ pressed }) => pressed && styles.pressed}>
        <View style={styles.sunset}>
          <Image
            source={{ uri: aiExplainedAssetUrl(data.assetPath) }}
            resizeMode="cover"
            style={styles.photoImage}
            accessibilityLabel={tx(language, data.alt || data.label || `Photo ${side}`)}
          />
          <View style={styles.photoLetter}><Text style={styles.photoLetterText}>{side}</Text></View>
        </View>
      </Pressable>
      <Text style={styles.voteCount}>{count ? voteCountText(count, language) : " "}</Text>
      <Pressable onPress={() => onVote(side)} style={styles.voteButton}>
        <Text style={styles.voteButtonText}>
          {tx(language, tier === "el" ? "AI made this one" : "This one was AI-generated")}
          {count ? ` · ${count}` : ""}
        </Text>
      </Pressable>
    </View>
  );
}


function AudioChoice({ round, side, count, leading, onVote, onPlay, isPlaying, tier, language = "en" }) {
  const data = round?.[String(side).toLowerCase()] || {};
  const actionLabel = tierText(round.actionLabel, tier) || (tier === "el" ? "AI made this voice" : "This is the synthetic voice");

  return (
    <View style={[styles.audioChoice, leading === side && styles.leadingCard]}>
      <Text style={styles.cardLabel}>🎙️ {tx(language, String(data.label || `Recording ${side}`)).toUpperCase()}</Text>
      <Text style={styles.audioPrompt}>Both recordings say the same sentence.</Text>

      <Pressable onPress={() => onPlay(side)} style={styles.audioPlayButton}>
        <Text style={styles.audioPlayButtonText}>
          {isPlaying ? tx(language, "❚❚ Pause") : (language === "es" ? `▶ Reproducir grabación ${side}` : `▶ Play recording ${side}`)}
        </Text>
      </Pressable>

      <Text style={styles.audioHint}>Listen as many times as you need before voting.</Text>

      <View style={styles.cardSpacer} />

      <Text style={styles.voteCount}>{count ? voteCountText(count, language) : " "}</Text>
      <Pressable onPress={() => onVote(side)} style={styles.voteButton}>
        <Text style={styles.voteButtonText}>
          {actionLabel}
          {count ? ` · ${count}` : ""}
        </Text>
      </Pressable>
    </View>
  );
}


function VideoChoice({ round, side, count, leading, onVote, tier, language = "en" }) {
  const data = round?.[String(side).toLowerCase()] || {};
  const actionLabel = tierText(round.actionLabel, tier) || (tier === "el" ? "AI made this one move" : "This uses AI-generated motion");

  return (
    <View style={[styles.videoChoice, leading === side && styles.leadingCard]}>
      <Text style={styles.cardLabel}>🎬 {tx(language, String(data.label || `Video ${side}`)).toUpperCase()}</Text>
      <View style={styles.videoFrame}>
        <video
          src={aiExplainedAssetUrl(data.videoPath)}
          controls
          playsInline
          muted
          preload="metadata"
          disablePictureInPicture
          controlsList="nodownload noplaybackrate"
          aria-label={tx(language, data.alt || data.label || `Video ${side}`)}
          style={{ width: "100%", height: "100%", objectFit: "cover", backgroundColor: "#000000" }}
        />
      </View>
      <Text style={styles.videoHint}>5-second silent clip · watch as many times as you need</Text>
      <View style={styles.cardSpacer} />
      <Text style={styles.voteCount}>{count ? voteCountText(count, language) : " "}</Text>
      <Pressable onPress={() => onVote(side)} style={styles.voteButton}>
        <Text style={styles.voteButtonText}>
          {actionLabel}
          {count ? ` · ${count}` : ""}
        </Text>
      </Pressable>
    </View>
  );
}

function ProvenanceCard({ item, language = "en" }) {
  if (!item) return null;
  return (
    <View style={styles.provenanceCard}>
      <Text style={styles.provenanceHeading}>{tx(language, item.heading)}</Text>
      {(item.lines || []).map((line) => (
        <Text key={line} style={styles.provenanceLine}>{tx(language, line)}</Text>
      ))}
    </View>
  );
}

export default function RealOrAIScreen({
  tier = "hs",
  language = "en",
  onBack = () => {},
  initialVote = null,
  startInReveal = false,
  onAttempt = async () => null,
  contentVersion = "v2_bilingual_rc1",
  onNarrationMoment = () => {},
  onBeforeMediaAudio = () => {},
}) {
  const initialVotes = initialVote
    ? {
        0: {
          A: initialVote === "A" ? 1 : 0,
          B: initialVote === "B" ? 1 : 0,
          last: initialVote,
        },
      }
    : {};

  const [roundIndex, setRoundIndex] = useState(0);
  const [mode, setMode] = useState(startInReveal ? "reveal" : "vote");
  const [votes, setVotes] = useState(initialVotes);
  const [recorded, setRecorded] = useState({});
  const [wrongCount, setWrongCount] = useState(0);
  const [statsByRound, setStatsByRound] = useState({});
  const [productionRounds, setProductionRounds] = useState([]);
  const [contentError, setContentError] = useState("");
  const roundAudioRef = useRef(null);
  const [playingAudioSide, setPlayingAudioSide] = useState(null);

  useEffect(() => {
    let active = true;
    getRealOrAIProductionRounds()
      .then((items) => { if (active) setProductionRounds(items); })
      .catch((error) => {
        if (active) setContentError(error?.message || "Exhibit content is temporarily unavailable.");
      });
    return () => { active = false; };
  }, []);

  const productionIds = new Set(productionRounds.map((item) => item.id));
  const rounds = productionRounds.length
    ? [...productionRounds, ...PROTOTYPE_ROUNDS.filter((item) => !productionIds.has(item.id))]
    : [];
  const round = rounds[roundIndex] || null;
  const currentVotes = votes[roundIndex] || { A: 0, B: 0, last: null };
  const leading = votePick(currentVotes);
  const played = Object.keys(recorded).length;
  const baseRoundContentVersion = round?.contentVersion || contentVersion;
  const roundContentVersion = language === "es"
    ? `${baseRoundContentVersion}_es`
    : baseRoundContentVersion;


  const stopRoundAudio = () => {
    if (roundAudioRef.current) {
      try {
        roundAudioRef.current.pause();
        roundAudioRef.current.currentTime = 0;
        roundAudioRef.current.src = "";
      } catch {}
    }
    setPlayingAudioSide(null);
  };

  const playRoundAudio = (side) => {
    const data = round?.[String(side).toLowerCase()] || {};
    if (!data.audioPath || typeof Audio === "undefined") return;

    if (!roundAudioRef.current) roundAudioRef.current = new Audio();

    const audio = roundAudioRef.current;
    const url = aiExplainedAssetUrl(data.audioPath);

    if (playingAudioSide === side && !audio.paused) {
      audio.pause();
      setPlayingAudioSide(null);
      return;
    }

    onBeforeMediaAudio();

    try {
      audio.pause();
      audio.currentTime = 0;
    } catch {}

    audio.src = url;
    audio.onended = () => setPlayingAudioSide(null);
    audio.onerror = () => setPlayingAudioSide(null);
    audio.play()
      .then(() => setPlayingAudioSide(side))
      .catch(() => setPlayingAudioSide(null));
  };

  useEffect(() => {
    stopRoundAudio();
    // Stop any voice sample when moving between rounds or into reveal mode.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roundIndex, mode]);

  useEffect(
    () => () => {
      if (roundAudioRef.current) {
        try {
          roundAudioRef.current.pause();
          roundAudioRef.current.src = "";
        } catch {}
      }
    },
    []
  );

  const recordRound = (index) => {
    if (recorded[index]) return;

    const currentRound = rounds[index];
    if (!currentRound) return;
    const v = votes[index] || { A: 0, B: 0, last: null };
    const pick = votePick(v) || "A";
    const wrong = pick !== roundCorrectAnswer(currentRound);

    setRecorded((old) => ({ ...old, [index]: true }));
    if (wrong) setWrongCount((old) => old + 1);
  };

  useEffect(() => {
    if (!productionRounds.length || !startInReveal || !initialVote || recorded[0]) return;
    recordRound(0);
    // Production content must be loaded before evaluating the attract-screen handoff.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productionRounds]);

  useEffect(() => {
    if (mode !== "reveal" || !round) return undefined;

    let active = true;
    getExhibitAttemptStats({
      stationId: "real-or-ai",
      itemId: round.id,
      contentVersion: roundContentVersion,
    })
      .then((stats) => {
        if (active) {
          setStatsByRound((old) => ({ ...old, [round.id]: stats }));
        }
      })
      .catch((error) => {
        console.warn("[AI EXPLAINED V2] Could not refresh attempt stats:", error?.message || error);
      });

    return () => {
      active = false;
    };
  }, [mode, round?.id, roundContentVersion]);

  const addVote = (side) => {
    if (mode !== "vote") return;

    // Every tap is an attempt. Repeated attempts by the same person/session
    // deliberately count in the aggregate right/wrong statistics.
    const correctAnswer = roundCorrectAnswer(round);
    onAttempt({
      stationId: "real-or-ai",
      itemId: round.id,
      selectedAnswer: side,
      correctAnswer,
      isCorrect: side === correctAnswer,
      attemptType: round.interactionMode === "trust"
        ? "source_trust_choice"
        : round.interactionMode === "manipulation"
        ? "manipulation_choice"
        : "binary_guess",
      contentVersion: roundContentVersion,
      metadata: {
        medium: tierText(round.medium, tier),
        source: "real_or_ai_screen",
        interaction_mode: round.interactionMode || "real_or_ai",
        language,
      },
    }).then((result) => {
      if (result?.stats) {
        setStatsByRound((old) => ({ ...old, [round.id]: result.stats }));
      }
    });

    setVotes((old) => {
      const existing = old[roundIndex] || { A: 0, B: 0, last: null };
      return {
        ...old,
        [roundIndex]: {
          ...existing,
          [side]: existing[side] + 1,
          last: side,
        },
      };
    });
  };

  const reveal = () => {
    if (!leading) return;
    recordRound(roundIndex);
    stopRoundAudio();
    const narrationId = REVEAL_NARRATION_BY_ROUND_ID[round?.id];
    if (narrationId) onNarrationMoment(narrationId);
    setMode("reveal");
  };

  const next = () => {
    if (roundIndex >= rounds.length - 1) {
      setMode("end");
      return;
    }

    setRoundIndex((old) => old + 1);
    setMode("vote");
  };

  const reset = () => {
    setRoundIndex(0);
    setMode("vote");
    setVotes({});
    setRecorded({});
    setWrongCount(0);
  };

  if (contentError) {
    return (
      <View style={styles.container}>
        <View style={styles.topBar}><Pressable onPress={onBack} style={styles.backButton}><Text style={styles.backText}>← Menu</Text></Pressable><Text style={styles.brand}>Real or AI?</Text></View>
        <View style={styles.contentState}><Text style={styles.contentStateTitle}>Exhibit content is temporarily unavailable.</Text><Text style={styles.contentStateBody}>{contentError}</Text></View>
      </View>
    );
  }

  if (!rounds.length || !round) {
    return (
      <View style={styles.container}>
        <View style={styles.topBar}><Pressable onPress={onBack} style={styles.backButton}><Text style={styles.backText}>← Menu</Text></Pressable><Text style={styles.brand}>Real or AI?</Text></View>
        <View style={styles.contentState}><Text style={styles.contentStateTitle}>Loading exhibit content…</Text></View>
      </View>
    );
  }

  if (mode === "end") {
    return (
      <View style={styles.container}>
        <View style={styles.topBar}>
          <Pressable onPress={onBack} style={styles.backButton}><Text style={styles.backText}>← Menu</Text></Pressable>
          <Text style={styles.brand}>Real or AI?</Text>
        </View>

        <View style={styles.endScreen}>
          <Text style={styles.endHeading}>
            {tier === "el" ? "You finished all the rounds!" : tier === "ms" ? "You made it to the top." : "You made it up the ladder."}
          </Text>

          <Text style={styles.endScore}>
            {tier === "el" ? "You missed " : "You missed "}
            <Text style={styles.accent}>{wrongCount} of {played}</Text>
            {tier === "el" ? " challenges." : " media-literacy challenges."}
          </Text>

          <Text style={styles.endBody}>
            {tier === "el"
              ? "The big lesson: looking or sounding real is not proof. Check where something came from, who is behind it, and whether it is pushing you to react before you can check."
              : tier === "ms"
              ? "The lesson is bigger than spotting AI. Style, polish, and a realistic voice are not proof. Check sources and provenance, and notice when a post uses urgency or emotion to push you past verification."
              : "The lesson is broader than AI detection: convincing surfaces are cheap. Check provenance and evidence, separate style from truth, and notice when emotional pressure is trying to outrun verification."}
          </Text>

          <View style={styles.actionRow}>
            <Pressable onPress={onBack} style={styles.primaryButton}><Text style={styles.primaryButtonText}>{tier === "el" ? "Pick another game →" : "Explore the exhibit →"}</Text></Pressable>
            <Pressable onPress={reset} style={styles.secondaryButton}><Text style={styles.secondaryButtonText}>Play again</Text></Pressable>
          </View>
        </View>
      </View>
    );
  }

  if (mode === "reveal") {
    const pick = votePick(currentVotes) || "A";
    const generatedSide = round.generated || "B";
    const generatedLabel = round.kind === "photo"
      ? tx(language, round[generatedSide.toLowerCase()]?.label || `Photo ${generatedSide}`)
      : tx(language, round[generatedSide.toLowerCase()]?.label || `Choice ${generatedSide}`);
    const stats = statsByRound[round.id] || null;
    const enoughStats = Number(stats?.scored_attempts || 0) >= 5;
    const wrong = pick !== roundCorrectAnswer(round);
    const customOutcome = wrong
      ? tierText(round.reveal?.outcome?.incorrect, tier)
      : tierText(round.reveal?.outcome?.correct, tier);

    return (
      <View style={styles.container}>
        <View style={styles.topBar}>
          <Pressable onPress={onBack} style={styles.backButton}><Text style={styles.backText}>← Menu</Text></Pressable>
          <Text style={styles.brand}>Real or AI?</Text>
          <Text style={styles.dots}>{roundIndex + 1} / {rounds.length}</Text>
        </View>

        <View style={styles.revealScreen}>
          <Text style={[styles.revealEyebrow, { color: wrong ? "#FBBF24" : "#5ADC9B" }]}>
            {customOutcome || (wrong
              ? tier === "el" ? "✗ IT FOOLED YOU" : "✗ YOU WERE FOOLED"
              : tier === "el" ? "✓ YOU GOT IT!" : "✓ YOU CAUGHT IT")}
          </Text>

          {round.reveal?.headline ? (
            <Text style={styles.revealLead}>{tierText(round.reveal.headline, tier)}</Text>
          ) : (
            <Text style={styles.revealLead}>
              {round.id === "outrage"
                ? tier === "el" ? "The angry one was " : "It was engineered: "
                : tier === "el" ? "The computer made " : "It was "}
              <Text style={styles.revealStrong}>{generatedLabel}</Text>
              {wrong
                ? tier === "el" ? " — and you picked the other one." : " — and you said the other one."
                : tier === "el" ? " — and you spotted it." : " — and you caught it."}
            </Text>
          )}

          {enoughStats ? (
            <>
              <Text style={styles.bigPct}>{stats.pct_incorrect}%</Text>
              <Text style={styles.pctCaption}>
                {round.reveal?.statsCaption
                  ? `${tierText(round.reveal.statsCaption, tier)} · ${stats.scored_attempts} attempts`
                  : tier === "el"
                  ? `of ${stats.scored_attempts} attempts got this one wrong`
                  : `wrong across ${stats.scored_attempts} recorded attempts`}
              </Text>
            </>
          ) : (
            <>
              <Text style={styles.bigPct}>{Number(stats?.scored_attempts || 0)}</Text>
              <Text style={styles.pctCaption}>
                {Number(stats?.scored_attempts || 0) === 1
                  ? "live attempt recorded so far"
                  : "live attempts recorded so far"}
              </Text>
            </>
          )}

          <Text style={styles.whyText}>
            {round.reveal?.body
              ? tierText(round.reveal.body, tier)
              : tierText(round.why, tier)}{" "}
            <Text style={styles.accent}>{tierText(round.clue, tier)}</Text>
          </Text>

          {round.reveal?.provenance ? (
            <View style={styles.provenanceRow}>
              <ProvenanceCard item={round.reveal.provenance.a} language={language} />
              <ProvenanceCard item={round.reveal.provenance.b} language={language} />
            </View>
          ) : null}

          <View style={styles.actionRow}>
            <Pressable onPress={next} style={styles.primaryButton}>
              <Text style={styles.primaryButtonText}>
                {nextRoundLabel(rounds, roundIndex, tier, language)}
              </Text>
            </Pressable>

            <Pressable onPress={onBack} style={styles.ghostButton}>
              <Text style={styles.ghostButtonText}>{tier === "el" ? "or pick another game" : "or explore the exhibit"}</Text>
            </Pressable>
          </View>

          <Text style={styles.footnote}>
            Live attempt statistics from Saras · repeated plays count as repeated attempts
          </Text>
        </View>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <View style={styles.topBar}>
        <Pressable onPress={onBack} style={styles.backButton}><Text style={styles.backText}>← Menu</Text></Pressable>
        <Text style={styles.brand}>Real or AI?</Text>
        <Text style={styles.dots}>{roundIndex + 1} / {rounds.length}</Text>
      </View>

      <View style={styles.voteScreen}>
        <Text style={styles.eyebrow}>
          {language === "es"
            ? `RONDA ${roundIndex + 1} · ${tx(language, tierText(round.medium, tier)).toUpperCase()}`
            : `ROUND ${roundIndex + 1} · ${tierText(round.medium, tier).toUpperCase()}`}
        </Text>
        <Text style={styles.heading}>{tierText(round.title, tier)}</Text>
        <Text style={styles.sub}>{tierText(round.sub, tier)}</Text>

        <View style={styles.pair}>
          {round.kind === "photo" ? (
            <>
              <PhotoChoice round={round} side="A" count={currentVotes.A} leading={leading} onVote={addVote} tier={tier} language={language} />
              <PhotoChoice round={round} side="B" count={currentVotes.B} leading={leading} onVote={addVote} tier={tier} language={language} />
            </>
          ) : round.kind === "voice" ? (
            <>
              <AudioChoice round={round} side="A" count={currentVotes.A} leading={leading} onVote={addVote} onPlay={playRoundAudio} isPlaying={playingAudioSide === "A"} tier={tier} language={language} />
              <AudioChoice round={round} side="B" count={currentVotes.B} leading={leading} onVote={addVote} onPlay={playRoundAudio} isPlaying={playingAudioSide === "B"} tier={tier} language={language} />
            </>
          ) : round.kind === "video" ? (
            <>
              <VideoChoice round={round} side="A" count={currentVotes.A} leading={leading} onVote={addVote} tier={tier} language={language} />
              <VideoChoice round={round} side="B" count={currentVotes.B} leading={leading} onVote={addVote} tier={tier} language={language} />
            </>
          ) : (
            <>
              <TextCard round={round} side="A" votes={currentVotes} leading={leading} onVote={addVote} tier={tier} language={language} />
              <TextCard round={round} side="B" votes={currentVotes} leading={leading} onVote={addVote} tier={tier} language={language} />
            </>
          )}
        </View>

        <Text style={styles.status}>
          {leading
            ? `${currentVotes.A + currentVotes.B} ${currentVotes.A + currentVotes.B === 1 ? "vote" : "votes"} · A ${currentVotes.A} · B ${currentVotes.B}${currentVotes.A === currentVotes.B ? " · tied" : ` · leaning ${leading}`}`
            : tier === "el" ? "Everyone gets a vote. Tap once each." : "Everyone votes — tap once per person."}
        </Text>

        {leading && (
          <Pressable onPress={reveal} style={styles.primaryButton}>
            <Text style={styles.primaryButtonText}>{tier === "el" ? "Show me the answer →" : "Reveal what happened →"}</Text>
          </Pressable>
        )}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: { width: 1920, height: 1080, backgroundColor: "#070D17" },
  topBar: { height: 78, paddingHorizontal: 56, flexDirection: "row", alignItems: "center" },
  backButton: { paddingVertical: 10, paddingRight: 26 },
  backText: { fontSize: 19, fontWeight: "800", color: "#8FA5BC" },
  brand: { fontSize: 19, fontWeight: "800", color: "#8FA5BC", letterSpacing: 0.8 },
  dots: { marginLeft: "auto", marginRight: 160, fontSize: 17, fontWeight: "900", color: "#5F7690", letterSpacing: 2 },
  voteScreen: { alignItems: "center", paddingHorizontal: 105 },
  eyebrow: { marginTop: 30, fontSize: 17, fontWeight: "900", letterSpacing: 2.5, color: "#4FD1FF" },
  heading: { marginTop: 10, maxWidth: 1500, textAlign: "center", fontSize: 58, lineHeight: 64, fontWeight: "900", color: "#F4F8FC" },
  sub: { marginTop: 12, fontSize: 26, color: "#A9BCCE", textAlign: "center" },
  pair: { marginTop: 38, flexDirection: "row", gap: 34, alignItems: "stretch" },
  photoChoice: { padding: 8, borderRadius: 30, alignItems: "center" },
  leadingPhotoChoice: { borderWidth: 3, borderColor: "#4FD1FF" },
  sunset: { width: 560, height: 350, borderRadius: 26, overflow: "hidden", position: "relative", backgroundColor: "#152238" },
  photoImage: { width: "100%", height: "100%" },
  photoLetter: { position: "absolute", left: 0, right: 0, bottom: 0, height: 60, backgroundColor: "rgba(4,10,18,0.45)", alignItems: "center", justifyContent: "center" },
  photoLetterText: { fontSize: 27, fontWeight: "900", color: "#FFFFFF" },
  textCard: { width: 720, minHeight: 365, paddingHorizontal: 42, paddingVertical: 36, borderRadius: 24, borderWidth: 1, borderColor: "#1B2A44", backgroundColor: "#0D1626" },
  leadingCard: { borderWidth: 3, borderColor: "#4FD1FF" },
  cardLabel: { fontSize: 18, fontWeight: "900", letterSpacing: 2.4, color: "#5F7690" },
  newsHead: { marginTop: 18, fontSize: 31, lineHeight: 37, fontWeight: "900", color: "#F4F8FC" },
  audioNote: { marginTop: 18, fontSize: 14, fontWeight: "900", letterSpacing: 1.2, color: "#4FD1FF" },
  audioChoice: { width: 720, minHeight: 365, paddingHorizontal: 42, paddingVertical: 36, borderRadius: 24, borderWidth: 1, borderColor: "#1B2A44", backgroundColor: "#0D1626" },
  videoChoice: { width: 720, minHeight: 500, paddingHorizontal: 32, paddingVertical: 28, borderRadius: 24, borderWidth: 1, borderColor: "#1B2A44", backgroundColor: "#0D1626" },
  videoFrame: { marginTop: 18, width: "100%", height: 300, borderRadius: 20, overflow: "hidden", backgroundColor: "#000000" },
  videoHint: { marginTop: 10, fontSize: 15, color: "#7F94AA", textAlign: "center" },
  audioPrompt: { marginTop: 28, fontSize: 25, lineHeight: 34, fontWeight: "800", color: "#E7EEF6", textAlign: "center" },
  audioPlayButton: { marginTop: 28, minHeight: 76, paddingHorizontal: 26, borderRadius: 18, backgroundColor: "#12344A", borderWidth: 2, borderColor: "#4FD1FF", alignItems: "center", justifyContent: "center" },
  audioPlayButtonText: { fontSize: 24, fontWeight: "900", color: "#F4F8FC" },
  audioHint: { marginTop: 14, fontSize: 16, color: "#7F94AA", textAlign: "center" },
  cardBody: { marginTop: 18, fontSize: 27, lineHeight: 40, color: "#E7EEF6" },
  cardSpacer: { flex: 1, minHeight: 18 },
  voteCount: { minHeight: 26, fontSize: 17, color: "#7F94AA", textAlign: "center" },
  voteButton: { marginTop: 10, minHeight: 54, paddingHorizontal: 22, borderRadius: 14, borderWidth: 1, borderColor: "#4FD1FF", backgroundColor: "#0E2A3B", alignItems: "center", justifyContent: "center" },
  voteButtonText: { fontSize: 18, fontWeight: "900", color: "#F4F8FC" },
  status: { marginTop: 22, fontSize: 19, fontWeight: "700", color: "#8FA5BC" },
  primaryButton: { marginTop: 18, minHeight: 62, paddingHorizontal: 30, borderRadius: 16, backgroundColor: "#0E354A", borderWidth: 1, borderColor: "#4FD1FF", alignItems: "center", justifyContent: "center" },
  primaryButtonText: { fontSize: 20, fontWeight: "900", color: "#F4F8FC" },
  secondaryButton: { marginTop: 18, minHeight: 62, paddingHorizontal: 30, borderRadius: 16, borderWidth: 1, borderColor: "#1B2A44", backgroundColor: "#0D1626", alignItems: "center", justifyContent: "center" },
  secondaryButtonText: { fontSize: 20, fontWeight: "900", color: "#B9C8D8" },
  ghostButton: { marginTop: 18, paddingHorizontal: 18, minHeight: 62, justifyContent: "center" },
  ghostButtonText: { fontSize: 19, fontWeight: "800", color: "#8FA5BC" },
  revealScreen: { alignItems: "center", paddingHorizontal: 160 },
  revealEyebrow: { marginTop: 78, fontSize: 20, fontWeight: "900", letterSpacing: 2.8 },
  revealLead: { marginTop: 16, fontSize: 30, lineHeight: 42, fontWeight: "800", color: "#9FB2C6", textAlign: "center" },
  revealStrong: { color: "#F4F8FC" },
  bigPct: { marginTop: 38, fontSize: 132, lineHeight: 140, fontWeight: "900", color: "#4FD1FF" },
  pctCaption: { fontSize: 36, fontWeight: "800", color: "#F4F8FC" },
  whyText: { marginTop: 30, maxWidth: 1280, fontSize: 28, lineHeight: 42, color: "#B9C8D8", textAlign: "center" },
  accent: { color: "#4FD1FF", fontWeight: "900" },
  actionRow: { marginTop: 36, flexDirection: "row", gap: 18, alignItems: "center" },
  footnote: { marginTop: 30, fontSize: 15, color: "#5F7690", textAlign: "center" },
  provenanceRow: { marginTop: 20, flexDirection: "row", gap: 18, justifyContent: "center" },
  provenanceCard: { width: 470, minHeight: 122, paddingHorizontal: 20, paddingVertical: 16, borderRadius: 18, borderWidth: 1, borderColor: "#29405F", backgroundColor: "#0D1626" },
  provenanceHeading: { fontSize: 17, fontWeight: "900", letterSpacing: 1.5, color: "#4FD1FF" },
  provenanceLine: { marginTop: 5, fontSize: 16, color: "#C7D5E3" },
  contentState: { flex: 1, alignItems: "center", justifyContent: "center", paddingHorizontal: 160 },
  contentStateTitle: { color: "#F4F8FC", fontSize: 42, fontWeight: "900", textAlign: "center" },
  contentStateBody: { marginTop: 16, color: "#FBBF24", fontSize: 19, textAlign: "center" },
  endScreen: { alignItems: "center", paddingHorizontal: 180 },
  endHeading: { marginTop: 130, fontSize: 72, lineHeight: 80, fontWeight: "900", color: "#F4F8FC", textAlign: "center" },
  endScore: { marginTop: 30, fontSize: 34, fontWeight: "800", color: "#F4F8FC", textAlign: "center" },
  endBody: { marginTop: 26, maxWidth: 1150, fontSize: 28, lineHeight: 43, color: "#B9C8D8", textAlign: "center" },
  pressed: { opacity: 0.9 },
});
